import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import type { AdminMe, AdminSearchHit } from "@vital/shared";
import {
  buildSystemSearchCatalog,
  entityHitsToSystemHits,
  groupSystemSearchHits,
  matchSystemSearch,
  normalizeSearchText,
  type SystemSearchHit,
} from "../lib/system-search";
import { api } from "../lib/api";
import { cmsStrings } from "../terminology";
import { LtrIsolate } from "./LtrIsolate";

type Props = {
  user: Pick<AdminMe, "role" | "platformRole" | "membershipRole">;
};

const DEBOUNCE_MS = 250;

export function SystemSearch({ user }: Props) {
  const navigate = useNavigate();
  const listboxId = useId();
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [recordItems, setRecordItems] = useState<AdminSearchHit[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);

  const catalog = useMemo(() => buildSystemSearchCatalog(user), [user]);
  const destHits = useMemo(() => matchSystemSearch(catalog, query), [catalog, query]);
  const recordHits = useMemo(() => entityHitsToSystemHits(recordItems), [recordItems]);
  const hits = useMemo(() => [...destHits, ...recordHits], [destHits, recordHits]);
  const groups = useMemo(() => groupSystemSearchHits(hits), [hits]);
  const liveQuery = normalizeSearchText(query).replace(/\s+/g, "").length >= 2;

  function closePalette() {
    setQuery("");
    setOpen(false);
    setRecordItems([]);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }

  function openPalette() {
    setOpen(true);
  }

  useEffect(() => {
    if (!liveQuery) {
      setRecordItems([]);
      setRecordsLoading(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setRecordsLoading(true);
      void api
        .searchAdmin(query.trim())
        .then((res) => {
          if (!cancelled) setRecordItems(res.items);
        })
        .catch(() => {
          if (!cancelled) setRecordItems([]);
        })
        .finally(() => {
          if (!cancelled) setRecordsLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [liveQuery, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    setActiveIndex((index) => {
      if (hits.length === 0) return 0;
      return Math.min(index, hits.length - 1);
    });
  }, [hits.length]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function onGlobalKey(e: globalThis.KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (open) {
          closePalette();
        } else {
          openPalette();
        }
        return;
      }
    }
    window.addEventListener("keydown", onGlobalKey);
    return () => window.removeEventListener("keydown", onGlobalKey);
  }, [open]);

  useEffect(() => {
    if (!open || hits.length === 0) return;
    const active = panelRef.current?.querySelector<HTMLElement>(
      `[data-search-index="${activeIndex}"]`,
    );
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, hits.length]);

  function selectHit(hit: SystemSearchHit) {
    setQuery("");
    setOpen(false);
    setRecordItems([]);
    navigate(hit.to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      if (query) {
        setQuery("");
      } else {
        closePalette();
      }
      return;
    }

    if (e.key === "Home" && hits.length > 0) {
      e.preventDefault();
      setActiveIndex(0);
      return;
    }

    if (e.key === "End" && hits.length > 0) {
      e.preventDefault();
      setActiveIndex(hits.length - 1);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (hits.length === 0) return;
      setActiveIndex((i) => (i + 1) % hits.length);
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (hits.length === 0) return;
      setActiveIndex((i) => (i - 1 + hits.length) % hits.length);
      return;
    }

    if (e.key === "Enter") {
      const hit = hits[activeIndex];
      if (hit) {
        e.preventDefault();
        selectHit(hit);
      }
    }
  }

  const showList = hits.length > 0 || recordsLoading;
  const emptyOpen = query.trim().length > 0 && hits.length === 0 && !recordsLoading;
  const activeId =
    hits[activeIndex] ? `${listboxId}-opt-${hits[activeIndex]!.id}` : undefined;

  let optionIndex = -1;

  return (
    <div className="system-search" data-vt-topbar-search data-vt-search-palette>
      <button
        ref={triggerRef}
        type="button"
        className="btn btn--ghost btn--sm system-search__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={cmsStrings.shell.systemSearchOpenAria}
        onClick={() => openPalette()}
      >
        <span>{cmsStrings.shell.systemSearchTrigger}</span>
        <kbd className="system-search__kbd">
          <LtrIsolate>{cmsStrings.shell.systemSearchShortcut}</LtrIsolate>
        </kbd>
      </button>

      {open && (
        <div className="system-search__layer">
          <button
            type="button"
            className="system-search__backdrop"
            tabIndex={-1}
            aria-label={cmsStrings.shell.closeMenu}
            onClick={() => closePalette()}
          />
          <div
            className="system-search__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <h2 id={titleId} className="visually-hidden">
              {cmsStrings.shell.systemSearchAria}
            </h2>
            <label className="system-search__field">
              <span className="visually-hidden">{cmsStrings.shell.systemSearchAria}</span>
              <input
                ref={inputRef}
                type="search"
                role="combobox"
                aria-expanded={true}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={activeId}
                aria-haspopup="listbox"
                placeholder={cmsStrings.shell.systemSearchPlaceholder}
                value={query}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </label>

            {showList && (
              <div
                id={listboxId}
                ref={panelRef}
                className="system-search__panel"
                role="listbox"
                aria-label={cmsStrings.shell.systemSearchResultsAria}
              >
                {!query.trim() && (
                  <p className="system-search__hint">{cmsStrings.shell.systemSearchHint}</p>
                )}
                {groups.map((group) => (
                  <div key={group.group} className="system-search__group" role="group" aria-label={group.group}>
                    <p className="system-search__group-title">{group.group}</p>
                    <ul className="system-search__list">
                      {group.items.map((hit) => {
                        optionIndex += 1;
                        const index = optionIndex;
                        const active = index === activeIndex;
                        return (
                          <li key={hit.id} role="presentation">
                            <button
                              type="button"
                              id={`${listboxId}-opt-${hit.id}`}
                              data-search-index={index}
                              role="option"
                              aria-selected={active}
                              className={`system-search__option${active ? " system-search__option--active" : ""}${hit.nested ? " system-search__option--nested" : ""}`}
                              onMouseEnter={() => setActiveIndex(index)}
                              onClick={() => selectHit(hit)}
                            >
                              <span className="system-search__option-label">{hit.label}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
                {recordsLoading && (
                  <p className="system-search__empty" role="status">
                    {cmsStrings.shell.loading}
                  </p>
                )}
              </div>
            )}

            {emptyOpen && (
              <div id={listboxId} className="system-search__panel" role="status" aria-live="polite">
                <p className="system-search__empty">{cmsStrings.shell.systemSearchEmpty}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
